// Cache for canvas context to avoid recreation on subsequent calls
let canvasContextCache = null;

/**
 * Warm up canvas context on first use (reduces first-time latency)
 */
function getCanvasContext(width, height) {
  if (canvasContextCache) {
    const canvas = canvasContextCache.canvas;
    canvas.width = width;
    canvas.height = height;
    return canvasContextCache.ctx;
  }
  
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { 
    willReadFrequently: false,
    alpha: false 
  });
  
  if (!ctx) {
    throw new Error("Canvas not supported");
  }
  
  // Cache for reuse
  canvasContextCache = { canvas, ctx };
  return ctx;
}

/**
 * Compress an image file using Canvas API
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width (default: 1920)
 * @param {number} options.maxHeight - Maximum height (default: 1920)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.8)
 * @param {number} options.maxSizeMB - Maximum file size in MB (default: 2)
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressImage(file, options = {}) {
  const {
    maxWidth = 1920,
    maxHeight = 1920,
    quality = 0.8,
    maxSizeMB = 2,
  } = options;

  // Early return: if file is already small enough, skip compression
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB <= maxSizeMB * 0.8) {
    // Check dimensions using a lightweight approach
    try {
      if (typeof createImageBitmap !== 'undefined') {
        const imgBitmap = await createImageBitmap(file);
        if (imgBitmap.width <= maxWidth && imgBitmap.height <= maxHeight) {
          imgBitmap.close();
          return file; // No compression needed
        }
        imgBitmap.close();
      }
    } catch (e) {
      // Fallback to regular compression if createImageBitmap fails
    }
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        // Use regular Image for canvas compatibility (createImageBitmap doesn't work well with canvas)
        const img = new Image();
        img.src = e.target.result;
        await new Promise((imgResolve, imgReject) => {
          img.onload = imgResolve;
          img.onerror = imgReject;
        });

        // Calculate new dimensions
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
        }

        // Use cached canvas context for better performance
        const ctx = getCanvasContext(width, height);
        const canvas = canvasContextCache.canvas;

        // Optimize canvas drawing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);

        // Try to compress with quality, reducing quality if file is too large
        let currentQuality = quality;
        const compress = (q) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Failed to compress image"));
                return;
              }

              const sizeMB = blob.size / (1024 * 1024);

              // If still too large and quality can be reduced, try again
              if (sizeMB > maxSizeMB && q > 0.1) {
                compress(Math.max(0.1, q - 0.1));
                return;
              }

              // Create new File object with compressed blob
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });

              resolve(compressedFile);
            },
            "image/jpeg",
            q
          );
        };

        compress(currentQuality);
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Compress avatar image (smaller dimensions for profile pictures)
 * @param {File} file - The image file to compress
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressAvatar(file) {
  return compressImage(file, {
    maxWidth: 500,
    maxHeight: 500,
    quality: 0.85,
    maxSizeMB: 0.5, // 500KB max for avatars
  });
}

/**
 * Compress cover image (larger dimensions for cover photos)
 * @param {File} file - The image file to compress
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressCoverImage(file) {
  return compressImage(file, {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.8,
    maxSizeMB: 1.5, // 1.5MB max for cover images
  });
}

/**
 * Compress general image upload
 * @param {File} file - The image file to compress
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressImageUpload(file) {
  return compressImage(file, {
    maxWidth: 1920,
    maxHeight: 1920,
    quality: 0.8,
    maxSizeMB: 2, // 2MB max
  });
}

